/*
 * <rec-add> — optimistic add-to-cart for the cart-drawer "You may also like"
 * recommendations (both normal items and the window-opener, which has a sibling
 * <quantity-input> stepper).
 *
 * Flow: on click it gives instant feedback (button -> "Added") and drops a
 * lightweight "pending" line into the cart list immediately, then fires the
 * real cart/add.js in the background. When that returns, the authoritative
 * server render replaces the whole drawer (removing the pending line, showing
 * the real line, and updating the subtotal / checkout total correctly).
 * Totals are therefore only ever shown from the server = never wrong.
 */
(function () {
  if (customElements.get('rec-add')) return;

  class RecAdd extends HTMLElement {
    constructor() {
      super();
      this.miniCart = document.querySelector('mini-cart');
      this.addEventListener('click', this.onClick.bind(this));
    }

    getQuantity() {
      var input = this.parentElement ? this.parentElement.querySelector('.quantity__input') : null;
      if (!input) return 1;
      var qty = parseInt(input.value, 10) || 1;
      var max = parseInt(input.max, 10);
      if (max && qty > max) qty = max;
      if (qty < 1) qty = 1;
      return qty;
    }

    markAdded() {
      this.classList.add('is-added');
      var label = this.querySelector('.cart-rec-item__add-label');
      if (label) {
        this._origLabel = label.textContent;
        label.textContent = 'Added';
      }
    }

    revert() {
      this.removeAttribute('disabled');
      this.classList.remove('is-added');
      var label = this.querySelector('.cart-rec-item__add-label');
      if (label && this._origLabel) label.textContent = this._origLabel;
      var pending = document.querySelector('.cart-rec-pending');
      if (pending) pending.remove();
    }

    insertPending(qty) {
      try {
        var card = this.closest('.cart-rec-item');
        if (!card) return;
        var imgEl = card.querySelector('.cart-rec-item__image img');
        var titleEl = card.querySelector('.cart-rec-item__title');
        var priceEl = card.querySelector('.cart-rec-item__price');
        var list = document.querySelector('#mini-cart cart-items .mini-cart__navigation');
        if (!list) return;

        var li = document.createElement('li');
        li.className = 'cart-rec-pending';
        li.setAttribute('aria-hidden', 'true');

        var html = '';
        if (imgEl) {
          html += '<span class="cart-rec-pending__img"><img src="' + imgEl.getAttribute('src') + '" alt="" width="56" height="56"></span>';
        }
        html += '<span class="cart-rec-pending__info">';
        html += '<span class="cart-rec-pending__title">' + (titleEl ? titleEl.textContent : '') + '</span>';
        if (priceEl) {
          html += '<span class="cart-rec-pending__price">' + priceEl.textContent.trim() + (qty > 1 ? ' × ' + qty : '') + '</span>';
        }
        html += '</span>';
        html += '<span class="cart-rec-pending__spinner"></span>';
        li.innerHTML = html;

        var firstItem = list.querySelector('li[data-variant-id]');
        if (firstItem) list.insertBefore(li, firstItem);
        else list.appendChild(li);
      } catch (e) {
        /* pending line is a nicety; never block the add over it */
      }
    }

    onClick() {
      var id = this.dataset.variantId;
      if (!id || this.hasAttribute('disabled')) return;

      var qty = this.getQuantity();

      // No cart drawer (or on the /cart page): fall back to a normal POST.
      if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
        Shopify.postLink(theme.routes.cart_add_url, { parameters: { id: id, quantity: qty } });
        return;
      }

      // Instant, optimistic feedback.
      this.setAttribute('disabled', true);
      this.markAdded();
      this.insertPending(qty);
      if (this.miniCart && this.miniCart.open) this.miniCart.open();

      var sections = this.miniCart ? this.miniCart.getSectionsToRender().map(function (s) { return s.id; }) : [];
      var body = JSON.stringify({
        id: id,
        quantity: qty,
        sections: sections,
        sections_url: window.location.pathname
      });

      fetch(theme.routes.cart_add_url, Object.assign({}, fetchConfig('javascript'), { body: body }))
        .then(function (r) { return r.json(); })
        .then(function (r) {
          if (r.status === 422) {
            this.revert();
            document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: r.description } }));
          } else {
            // Authoritative render: replaces the whole drawer (pending line gone,
            // real line shown, subtotal + checkout total correct).
            if (this.miniCart) this.miniCart.renderContents(r);
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: r } }));
          }
        }.bind(this))
        .catch(function (e) {
          console.error(e);
          this.revert();
        }.bind(this));
    }
  }

  customElements.define('rec-add', RecAdd);
})();

/*
 * <cart-recs-slot> — lazily loads the cart-recs section into the drawer.
 * Fetches on connect (NOT via IntersectionObserver) so it works even though the
 * placeholder starts empty/zero-size — an empty element never "intersects", which
 * is why the observer-based approach left the recommendations blank.
 */
(function () {
  if (customElements.get('cart-recs-slot')) return;

  class CartRecsSlot extends HTMLElement {
    connectedCallback() {
      if (this._loaded) return;
      this._loaded = true;
      var url = this.dataset.url;
      if (!url) return;
      var self = this;
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var tmp = document.createElement('div');
          tmp.innerHTML = text;
          var recs = tmp.querySelector('cart-recommendations');
          if (recs && recs.innerHTML.trim().length) {
            self.innerHTML = recs.innerHTML;
            // Replay the drawer's slide-in reveal on the async recs: set the start
            // state, force a reflow to commit it, then transition to shown.
            self.classList.add('cart-recs-slot--anim');
            void self.offsetWidth;
            requestAnimationFrame(function () {
              self.classList.add('cart-recs-slot--in');
            });
          }
        })
        .catch(function (e) { console.error(e); });
    }
  }

  customElements.define('cart-recs-slot', CartRecsSlot);
})();
